/**
 * Mobile Accounts List Component
 * 
 * Displays accounts in a mobile-optimized list format with expandable details.
 */

import React, { ReactElement } from "react";
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  IconButton,
  Collapse,
} from "@mui/material";
import CopyFeedbackTooltip from "../../../components/CopyFeedbackTooltip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Account } from "../../../types";
import { AccountGroupDoc } from "../../Accounts/hooks/useAccountGroups";

export interface MobileAccountsListProps {
  accountGroups: AccountGroupDoc[];
  groupedActiveAccounts: Record<string, Account[]>;
  groupedArchivedAccounts: Record<string, Account[]>;
  getGroupBalance: (groupName: string) => number;
  getArchivedGroupBalance: (groupName: string) => number;
  getAccountDetailsForDisplay: (account: Account) => Array<{ name: string; value: string | number }>;
  expandedAccountDetails: Set<string>;
  onToggleDetails: (accountId: string) => void;
  onCopyToClipboard: (text: string, feedbackKey?: string) => Promise<void>;
  lastCopiedKey: string | null;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, account: Account) => void;
  formatCurrency: (amount: number, currency: string) => string;
  valuesVisible: boolean;
  defaultCurrency: string;
}

/**
 * Mobile accounts list component
 */
export default function MobileAccountsList({
  accountGroups,
  groupedActiveAccounts,
  groupedArchivedAccounts,
  getGroupBalance,
  getArchivedGroupBalance,
  getAccountDetailsForDisplay,
  expandedAccountDetails,
  onToggleDetails,
  onCopyToClipboard,
  lastCopiedKey,
  onMenuOpen,
  formatCurrency,
  valuesVisible,
  defaultCurrency,
}: MobileAccountsListProps): ReactElement {
  const [showArchived, setShowArchived] = React.useState(false);

  const renderAccountCard = (account: Account) => {
    const isExpanded = expandedAccountDetails.has(account.id);
    const accountDetails = getAccountDetailsForDisplay(account);
    const accountAmount = account.amount || 0;
    const isPositive = accountAmount >= 0;

    return (
      <Paper
        key={account.id}
        elevation={0}
        sx={{
          p: 1,
          mb: 0.75,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          borderRadius: 1.5,
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: 1,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: account.description ? 0.5 : 0 }}>
          <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
            <Typography variant="body2" fontWeight="medium" sx={{ fontSize: "0.875rem" }}>
              {account.accountName}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant="body1"
              color={isPositive ? "primary.main" : "error.main"}
              fontWeight="600"
              sx={{ minWidth: 85, textAlign: "right", fontSize: "0.875rem" }}
            >
              {valuesVisible ? formatCurrency(accountAmount, account.currency) : "•••••"}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => onMenuOpen(e, account)}
              sx={{ p: 0.25, minWidth: 32, width: 32, height: 32 }}
            >
              <MoreVertIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Box>
        </Box>

        {account.description && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontSize: "0.75rem", display: "block" }}>
            {account.description}
          </Typography>
        )}

        {accountDetails.length > 0 && (
          <Box>
            <Box
              onClick={() => onToggleDetails(account.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                py: 0.25,
                mt: 0.5,
                borderRadius: 0.75,
                transition: "background-color 0.2s ease",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, fontSize: "0.7rem" }}>
                {isExpanded ? "Hide details" : "Show details"}
              </Typography>
              {isExpanded ? (
                <ExpandLessIcon sx={{ fontSize: "0.875rem" }} color="action" />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: "0.875rem" }} color="action" />
              )}
            </Box>
            <Collapse in={isExpanded}>
              <Box sx={{ mt: 0.75, pt: 0.75, borderTop: 1, borderColor: "divider" }}>
                <Stack spacing={0.75}>
                  {accountDetails.map((detail, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                        {detail.name}:
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                        <Typography variant="caption" fontWeight="500" sx={{ fontSize: "0.75rem" }}>
                          {typeof detail.value === "string" ? detail.value : formatCurrency(detail.value, defaultCurrency)}
                        </Typography>
                        {(() => {
                          const copyKey = `mobile-${account.id}-${detail.name}`;
                          return (
                            <CopyFeedbackTooltip copyLabel="Copy" feedbackKey={copyKey} lastCopiedKey={lastCopiedKey} isMobile={true}>
                              <IconButton
                                size="small"
                                onClick={() => onCopyToClipboard(String(detail.value), copyKey)}
                                sx={{ p: 0.125, minWidth: 24, width: 24, height: 24 }}
                              >
                                <ContentCopyIcon sx={{ fontSize: "0.75rem" }} />
                              </IconButton>
                            </CopyFeedbackTooltip>
                          );
                        })()}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Collapse>
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 1 }}>
      {/* Active Accounts */}
      <Stack spacing={0.25}>
        {accountGroups.map((group) => {
          const groupAccounts = groupedActiveAccounts[group.name];
          if (!groupAccounts || groupAccounts.length === 0) return null;

          const groupBalance = getGroupBalance(group.name);
          return (
            <Accordion
              key={group.id}
              defaultExpanded
              sx={{
                "& .MuiAccordionSummary-root": { minHeight: 40, py: 0.25, px: 1 },
                "& .MuiAccordionDetails-root": { px: 1, pt: 0.75, pb: 0.75 },
                "&:before": { display: "none" },
                boxShadow: "none",
                border: 1,
                borderColor: "divider",
                borderRadius: 1.5,
                mb: 0.25,
                "&.Mui-expanded": {
                  mb: 0.25,
                },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: "1.125rem" }} />}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
                  <Typography variant="body2" fontWeight="600" sx={{ fontSize: "0.8125rem" }}>
                    {group.name} ({groupAccounts.length})
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="500"
                    sx={{ minWidth: 85, textAlign: "right", fontSize: "0.75rem" }}
                  >
                    {valuesVisible ? formatCurrency(groupBalance, defaultCurrency) : "•••••"}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={0.75}>
                  {groupAccounts.map((account) => renderAccountCard(account))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>

      {/* Archived Accounts Section */}
      {Object.keys(groupedArchivedAccounts).some(group => groupedArchivedAccounts[group].length > 0) && (
        <Box sx={{ mt: 1 }}>
          <Accordion
            expanded={showArchived}
            onChange={() => setShowArchived(!showArchived)}
            sx={{
              "& .MuiAccordionSummary-root": { minHeight: 40, py: 0.25, px: 1 },
              "& .MuiAccordionDetails-root": { px: 1, pt: 0.75, pb: 0.75 },
              "&:before": { display: "none" },
              boxShadow: "none",
              border: 1,
              borderColor: "divider",
              borderRadius: 1.5,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: "1.125rem" }} />}>
              <Typography variant="body2" fontWeight="600" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                Archived Accounts
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={0.75}>
                {accountGroups.map((group) => {
                  const groupAccounts = groupedArchivedAccounts[group.name];
                  if (!groupAccounts || groupAccounts.length === 0) return null;

                  const groupBalance = getArchivedGroupBalance(group.name);
                  return (
                    <Accordion
                      key={`archived-${group.id}`}
                      defaultExpanded
                      sx={{
                        "& .MuiAccordionSummary-root": { minHeight: 36, py: 0.25, px: 1 },
                        "& .MuiAccordionDetails-root": { px: 1, pt: 0.75, pb: 0.75 },
                        "&:before": { display: "none" },
                        boxShadow: "none",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1.5,
                        mb: 0.25,
                        "&.Mui-expanded": {
                          mb: 0.25,
                        },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: "1rem" }} />}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="500" sx={{ fontSize: "0.75rem" }}>
                            {group.name} ({groupAccounts.length})
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="500"
                            sx={{ minWidth: 80, textAlign: "right", fontSize: "0.7rem" }}
                          >
                            {valuesVisible ? formatCurrency(groupBalance, defaultCurrency) : "•••••"}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={0.75}>
                          {groupAccounts.map((account) => renderAccountCard(account))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Box>
  );
}
